/* eslint space-before-function-paren: 0, no-unused-vars: 0, padded-blocks: 0,
 no-trailing-spaces: 0 */

import { diffObjects, idOf } from './object-differ'

/**
 * Provides utilities that use the object-differ's diffs in order to describe
 * changes to configuration blobs that can be used for both audit logging
 * and security checks.
 */

const INDENT = '    '

export function typeToChanges(left, diff) {

  let typeToChanges = {}

  function entChanges(ent) {
    if (ent.hasOwnProperty('entityType')) {
      if (!typeToChanges.hasOwnProperty(ent.entityType)) {
        typeToChanges[ent.entityType] =
          {added: false, removed: false, replaced: false, modifiedFields: {}}
      }
      return typeToChanges[ent.entityType]
    } else {
      return {added: false, removed: false, replaced: false, modifiedFields: {}}
    }
  }

  function typeChanges(type) {
    if (typeToChanges.hasOwnProperty(type)) {
      return typeToChanges[type]
    } else {
      let changes = {added: false, removed: false, replaced: false, modifiedFields: {}}
      typeToChanges[type] = changes
      return changes
    }
  }

  visitDiff(left, diff, (ent, path, changes) => {
    changes.forEach(change => {
      switch (change.changeType) {
        case 'FIELD_ADDED':
          entChanges(ent).modifiedFields[change.fieldName] = true

          findChildEntTypes(change.value)
            .forEach(childType => { typeChanges(childType).added = true })

          break
        case 'FIELD_REPLACED':
          entChanges(ent).modifiedFields[change.fieldName] = true

          findChildEntTypes(change.value)
            .forEach(childType => { typeChanges(childType).replaced = true })

          break
        case 'FIELD_REMOVED':
          entChanges(ent).modifiedFields[change.fieldName] = true

          findChildEntTypes(ent[change.fieldName])
            .forEach(childType => { typeChanges(childType).removed = true })

          break
        case 'SIMPLE_FIELD_CHANGED':
          entChanges(ent).modifiedFields[change.fieldName] = true

          if (change.newValue === null) {
            findChildEntTypes(ent[change.fieldName])
              .forEach(childType => { typeChanges(childType).removed = true })
          }

          break
        case 'ARRAY_ELEM_ADDED':

          entChanges(ent).modifiedFields[change.fieldName] = true

          findChildEntTypes(change.elem)
            .forEach(childType => { typeChanges(childType).added = true })

          break
        case 'ARRAY_ELEM_REPLACED':

          entChanges(ent).modifiedFields[change.fieldName] = true

          findChildEntTypes(change.elem)
            .forEach(childType => { typeChanges(childType).replaced = true })

          break
        case 'ARRAY_SIMPLE_ELEM_REMOVED':
          entChanges(ent).modifiedFields[change.fieldName] = true
          break
        case 'ARRAY_ENTITY_REMOVED':
          let removedEnt = ent[change.fieldName].filter(it => entId(it) === change.entId)[0]
          entChanges(removedEnt).removed = true

          findChildEntTypes(removedEnt)
            .forEach(childType => { typeChanges(childType).removed = true })

          break
      }
    })
  })

  return typeToChanges
}

function findChildEntTypes(ent) {
  let childEntTypes = {}

  if (ent === null || typeof ent !== 'object') {
    return []
  }

  let unvisited = [ent]

  while (unvisited.length > 0) {
    let cur = unvisited.pop()

    if (cur.hasOwnProperty('entityType')) {
      childEntTypes[cur['entityType']] = true
    }

    Object.keys(cur).forEach(fieldName => {
      let value = cur[fieldName]
      if (Array.isArray(value)) {
        value.forEach(elem => {
          if (elem !== null && typeof elem === 'object') {
            unvisited.push(elem)
          }
        })
      } else if (value !== null && typeof value === 'object') {
        unvisited.push(value)
      }
    })
  }

  return Object.keys(childEntTypes)
}

export function describeDifferences(left, right) {

  let diff = diffObjects(left, right)

  return describeDiff(left, diff)
}

export function describeDiff(base, diff) {

  let lastPath = []

  let out = []

  visitDiff(base, diff, (ent, path, changes) => {
    if (!ent) { return 'noent' }
    if (changes.length > 0) {
      describePath(path, out)
      out.push(':  \n')
    }

    lastPath = path.slice()

    changes.forEach(change => {
      addIndent(1, out)
      describeChange(ent, change, 1, out)
      out.push('\n')
    })
  })

  return out.join('')
}

function describeChange(ent, change, level, out) {
  switch (change.changeType) {
    case 'FIELD_ADDED':
      out.push('- added field "')
      out.push(change.fieldName)
      out.push('" with value ')
      appendPossibleEntity(change.value, level, out)
      appendType(change.value, out)
      break
    case 'FIELD_REPLACED':
      out.push('- replaced field "' + change.fieldName + '" with ')
      appendPossibleEntity(change.value, level, out)
      appendType(change.value, out)
      break
    case 'FIELD_REMOVED':
      out.push('- removed field "')
      out.push(change.fieldName)
      out.push('" with value ')
      appendPossibleEntity(ent[change.fieldName], level, out)
      appendType(ent[change.fieldName], out)
      break
    case 'SIMPLE_FIELD_CHANGED':
      out.push('- changed field "' + change.fieldName + '" from "')
      out.push(ent[change.fieldName])
      out.push('"')
      appendType(ent[change.fieldName], out)
      out.push(' to "')
      out.push(change.newValue)
      out.push('"')
      appendType(change.newValue, out)
      break
    case 'ARRAY_ELEM_ADDED':
      out.push('- added to array "')
      out.push(change.fieldName)
      out.push('": ')
      appendPossibleEntity(change.elem, level, out)
      appendType(change.elem, out)
      break
    case 'ARRAY_ELEM_REPLACED':
      out.push('- replaced in array "' + change.fieldName + '" entry with id "' + idOf(change.elem) + '" to ')
      appendPossibleEntity(change.elem, level, out)
      appendType(change.elem, out)
      break
    case 'ARRAY_SIMPLE_ELEM_REMOVED':
      out.push('- removed from array "')
      out.push(change.fieldName)
      out.push('": "')
      out.push(change.elem)
      out.push('"')
      appendType(change.elem, out)
      break
    case 'ARRAY_ENTITY_REMOVED':
      let removedEnt = ent[change.fieldName].filter(it => entId(it) === change.entId)[0]
      out.push('- removed from array "')
      out.push(change.fieldName)
      out.push('": ')
      appendPossibleEntity(removedEnt, level, out)
      break
  }
}

function entId(ent) {
  if (ent === null || typeof ent !== 'object') {
    return null
  } else if (ent.hasOwnProperty('$identifier')) {
    return ent[ent['$identifier']]
  } else {
    return ent.id
  }
}

function appendType(object, out) {
  out.push(' (')
  out.push(typeof object)
  out.push(')')
}

function appendPossibleEntity(object, level, out) {
  if (typeof object === 'object') {
    JSON.stringify(object, null, INDENT).split('\n').forEach((line, i) => {
      if (i > 0) {
        out.push('\n')
      }
      addIndent(level, out)
      out.push(line)
    })
  } else {
    out.push('"')
    out.push(object)
    out.push('"')
  }
}

function addIndent(level, out) {
  for (let i = 0; i < level; i++) {
    out.push(INDENT)
  }
}

function describePath(path, out) {
  if (!path.length) {
    out.push('Top Level')
  }
  path.forEach((it, index) => {
    if (index !== 0 && it.change.changeType.indexOf('ENTITY_CHANGED') !== -1) {
      out.push('.')
    }

    describePathStep(it, out)
  })
}

function describePathStep(step, out) {

  switch (step.change.changeType) {
    case 'ENTITY_CHANGED':
      out.push(step.change.fieldName)
      break
    case 'ARRAY_ENTITY_CHANGED':
      out.push(step.change.fieldName)
      out.push('[')
      if (step.ent.hasOwnProperty('name')) {
        out.push('"')
        out.push(step.ent.name)
        out.push('" / ')
      }
      out.push('id = ')
      out.push(entId(step.ent))
      out.push(']')
      break
  }
}

export function visitDiff(base, diff, visitor) {
  return visitDiffHelper(base, [], diff, visitor)
}

function visitDiffHelper(base, path, diff, visitor) {

  let localChanges = diff.changes.filter(change => !isEntityChange(change))
  let childChanges = diff.changes.filter(isEntityChange)

  visitor(base, path, localChanges)

  childChanges.forEach(change => {
    if (!base[change.fieldName]) {
      return
    }
    switch (change.changeType) {
      case 'ENTITY_CHANGED':
        path.push({ent: base[change.fieldName], change: change})
        visitDiffHelper(base[change.fieldName], path.slice(), change.diff, visitor)
        break
      case 'ARRAY_ENTITY_CHANGED':
        let ent = base[change.fieldName].filter(ent => entId(ent) === change.entId)[0]
        path.push({ent: ent, change: change})
        visitDiffHelper(ent, path.slice(), change.entDiff, visitor)
        break
      default:
        throw new Error('Unhandled change type: ' + change.changeType)
    }

    path.pop()
  })

}

function isEntityChange(change) {
  return change.changeType === 'ENTITY_CHANGED' ||
      change.changeType === 'ARRAY_ENTITY_CHANGED'
}

function isSamePath(a, b) {

  if (typeof a !== typeof b) {
    return false
  }

  if (a.length !== b.length) {
    return false
  }

  for (let i = 0; i < a.length; i++) {
    let aChangeProps = Object.keys(a[i].change)
    let bChangeProps = Object.keys(b[i].change)

    if (aChangeProps.length !== bChangeProps.length) {
      return false
    }

    for (let i2 = 0; i2 < aChangeProps.length; i2++) {
      const propName = aChangeProps[i2]
      if (a[i].change[propName] !== b[i].change[propName]) {
        return false
      }
    }
  }

  return true
}
